import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import { BackButton } from "../scanner/back-button";
import { Box, Button, ButtonBase, Slide, Stack } from "@mui/material";
import {
  requestCameraPermission,
  queryCameraPermission,
} from "../../services/camera-permission";
import {
  PrescanConfigureScreen,
  SCAN_USER_SET,
  SCAN_FOR_SELF,
  SCAN_FOR_OTHER,
} from "./prescan-configure-screen";
import { TEMP_TUTORIAL_FORMATS } from "../../data/temp-tutorial-formats";
import { TutorialChecklist } from "./tutorial-checklist";
import { TutorialScanPlayer } from "./tutorial-scan-player";

// Swipe handling
const SWIPE_THRESHOLD = 60;
const useSwipe = ({ onSwipeLeft, onSwipeRight }) => {
  const startRef = useRef(null);
  return {
    onTouchStart: (event) => {
      const touch = event.touches[0];
      startRef.current = { x: touch.clientX, y: touch.clientY };
    },
    onTouchEnd: (event) => {
      if (!startRef.current) {
        return;
      }
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startRef.current.x;
      const dy = touch.clientY - startRef.current.y;
      startRef.current = null;

      // Ignore short or mostly vertical gestures (e.g., scrolling the list).
      if (Math.abs(dx) <= SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) {
        return;
      }

      // Call handler based on swipe direction.
      if (dx < 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    },
  };
};

// Page types
const PAGE_TYPE_CONFIGURE = "configure";
const PAGE_TYPE_PRESCAN = "prescan";
const PAGE_TYPE_SCAN = "scan";
const PAGE_TYPE_PERMISSION = "permission";
const PAGE_TYPE_WRONGSCAN = "wrongscan";

// The view component
export const PrescanSlideView = ({
  bodyPart,
  onBack,
  onContinue,
  onPermissionGranted,
  motionPermission,
  onRequestMotion,
  initialScanPov,
  mayUsePaper,
  onScanPovSet,
  onPaperTypeSet,
}) => {
  // Tutorial format
  const tutorialFormat = TEMP_TUTORIAL_FORMATS.find(
    (format) => format.id === bodyPart,
  );
  const scanVideoUrl = tutorialFormat.scan[0].url;

  // Camera permission
  const [permissionGrantedOnLoad, setPermissionGrantedOnLoad] = useState(null);
  useEffect(() => {
    let cancelled = false;
    queryCameraPermission().then((state) => {
      if (!cancelled) {
        const alreadyGranted = state === "granted";
        setPermissionGrantedOnLoad(alreadyGranted);

        // If granted on load, let the scanner warm up the camera right away.
        if (alreadyGranted && onPermissionGranted) {
          onPermissionGranted();
        }
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Camera permission request
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [motionDenied, setMotionDenied] = useState(false);
  const requestingRef = useRef(false);

  // Whether the motion sensor still needs a permission tap (iOS only).
  // Latched on first render so the page list stays stable once it's granted.
  const [motionNeededOnLoad] = useState(
    Boolean(onRequestMotion) && motionPermission === "prompt",
  );

  // Permission format
  const MOTION_ITEM_ID = 1;
  const permission = [
    {
      id: 0,
      type: "img",
      url: `${process.env.PUBLIC_URL}/tutorials/camera-permission.png`,
      imageProp: {
        maxWidth: 60,
        align: "top",
      },
      text: "Next, grant camera permission for scanning.",
      checkLabel: "Grant permission",
    },
    ...(motionNeededOnLoad
      ? [
          {
            id: MOTION_ITEM_ID,
            type: "img",
            url: `${process.env.PUBLIC_URL}/tutorials/motion-permission.png`,
            imageProp: { maxWidth: 60, align: "top" },
            text: "Also enable the motion sensor to aid in device positioning.",
            checkLabel: "Enable motion sensor",
          },
        ]
      : []),
  ];
  const permissionInstruction = permissionDenied
    ? "Camera access is required to proceed. Allow it in your browser settings then try again."
    : motionDenied
      ? "Motion sensor access is required to proceed. Fully close and reopen your browser to be prompted again."
      : null;

  // Permission function
  const onPermissionTap = async (item, checked) => {
    if (checked) {
      // Permission already granted; keep the item checked.
      return true;
    }

    // Don't make double requests.
    if (requestingRef.current) {
      return false;
    }

    // Check for motion permission first.
    // Only a "granted" answer checks the row, so a refusal (or a failed
    // request) keeps the Next button locked.
    if (item.id === MOTION_ITEM_ID) {
      requestingRef.current = true;
      const result = await onRequestMotion();
      requestingRef.current = false;
      const motionGranted = result === "granted";
      setMotionDenied(!motionGranted);
      return motionGranted;
    }

    // Request for camera permission.
    requestingRef.current = true;
    const state = await requestCameraPermission();
    requestingRef.current = false;
    const granted = state === "granted";
    const denied = state === "denied";

    // Update UI states according to the permission request result.
    setPermissionDenied(denied);
    setPermissionGranted(granted);
    if (granted && onPermissionGranted) {
      onPermissionGranted();
    }
    return granted;
  };

  // Slide tracking
  const needsPermissionPage = !permissionGrantedOnLoad || motionNeededOnLoad;
  const getPages = () => {
    const pages = initialScanPov === SCAN_USER_SET ? [PAGE_TYPE_CONFIGURE] : [];
    const tutorialPages = needsPermissionPage
      ? [PAGE_TYPE_SCAN, PAGE_TYPE_PERMISSION, PAGE_TYPE_PRESCAN]
      : [PAGE_TYPE_SCAN, PAGE_TYPE_PRESCAN];
    pages.push(...tutorialPages);
    return pages;
  };
  const pages = getPages();
  const [page, setPage] = useState(0);
  const [furthestPage, setFurthestPage] = useState(0);
  const [slideDirection, setSlideDirection] = useState("left");
  const pageType = pages[page];
  const isLastPage = page === pages.length - 1;

  // Page tracking
  const [checklistComplete, setChecklistComplete] = useState(false);
  const canGoNext = checklistComplete || page < furthestPage;
  const canGoBack = page > 0;
  const maxReachablePage = Math.min(
    checklistComplete ? Math.max(furthestPage, page + 1) : furthestPage,
    pages.length - 1,
  );
  const goToPage = (target) => {
    setSlideDirection(target > page ? "left" : "right");
    setPage(target);
    setChecklistComplete(false);
  };
  const onDotTap = (target) => {
    if (target !== page && target <= maxReachablePage) {
      goToPage(target);
    }
  };
  const onNext = () => {
    if (isLastPage) {
      onContinue(false);
    } else {
      goToPage(page + 1);
    }
  };
  const markPageChecked = (checked) => {
    if (!checked) return;
    setChecklistComplete(checked);
    setFurthestPage((previous) => Math.max(previous, page + 1));
  };

  // Local prescan configuration states & handlers
  const [scanPov, setScanPov] = useState(null);
  const [paperType, setPaperType] = useState(null);
  const onLocalScanPovChanged = (target) => {
    setScanPov(target);
    onScanPovSet(target);
  };
  const onLocalPaperTypeChanged = (type) => {
    setPaperType(type);
    onPaperTypeSet(type);
  };
  const prescanConfigured =
    scanPov !== null && (!mayUsePaper || paperType !== null);
  useEffect(() => {
    if (pageType === PAGE_TYPE_CONFIGURE && prescanConfigured) {
      markPageChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType, prescanConfigured]);

  // Swipe handling
  const swipeHandlers = useSwipe({
    onSwipeLeft: () => {
      if (canGoNext && !isLastPage) {
        onNext();
      }
    },
    onSwipeRight: () => {
      if (canGoBack) {
        goToPage(page - 1);
      }
    },
  });

  // The component
  return (
    <Box
      gap={4}
      sx={{
        overflow: "hidden",
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        backgroundColor: "background.default",
        display: "flex",
        flexDirection: "column",
        touchAction: "pan-y",
        pt: "max(24px, env(safe-area-inset-top))",
      }}
    >
      {/* Back button, available only when the dashboard is available */}
      {onBack && (
        <Box
          sx={{
            position: "absolute",
            top: "max(24px, env(safe-area-inset-top))",
            left: 8,
          }}
        >
          <BackButton icon="close" onClick={onBack} />
        </Box>
      )}

      {/* Pages -- each sliding in from the right */}
      <Box sx={{ 
        flex: 1, 
        minHeight: 0, 
        display: "flex", 
        overflow: "hidden",
        mt: 1,
      }}>
        <Slide key={pageType} direction={slideDirection} in appear={true}>
          <Box
            {...swipeHandlers}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {pageType === PAGE_TYPE_CONFIGURE && (
              <PrescanConfigureScreen
                mayUsePaper={mayUsePaper}
                scanPov={scanPov}
                paperType={paperType}
                onScanPovSet={onLocalScanPovChanged}
                onPaperTypeSet={onLocalPaperTypeChanged}
              />
            )}
            {pageType === PAGE_TYPE_PRESCAN && (
              <TutorialChecklist
                key={PAGE_TYPE_PRESCAN}
                list={tutorialFormat.prescan}
                initialChecked={page < furthestPage}
                onAllCheckUpdate={markPageChecked}
              />
            )}
            {pageType === PAGE_TYPE_SCAN && (
              <TutorialScanPlayer
                url={scanVideoUrl}
                onCompletePlayback={() => markPageChecked(true)}
              />
            )}
            {pageType === PAGE_TYPE_PERMISSION && (
              <TutorialChecklist
                key={PAGE_TYPE_PERMISSION}
                list={permission}
                initialChecked={permissionGranted}
                onAllCheckUpdate={setChecklistComplete}
                listInstruction={permissionInstruction}
                onItemTap={onPermissionTap}
              />
            )}
            {pageType === PAGE_TYPE_WRONGSCAN && (
              <TutorialChecklist
                key={PAGE_TYPE_WRONGSCAN}
                list={tutorialFormat.wrongScan}
                onAllCheckUpdate={setChecklistComplete}
                listInstruction={null}
              />
            )}
          </Box>
        </Slide>
      </Box>

      {/* Actions */}
      <Stack
        spacing={2}
        sx={{ px: 4, pb: "max(24px, env(safe-area-inset-bottom))" }}
      >
        <PageDots
          index={page}
          total={pages.length}
          maxReachable={maxReachablePage}
          onSelect={onDotTap}
        />
        <NextButton
          label={isLastPage ? "Start Scanning" : "Next"}
          disabled={!canGoNext}
          onTap={onNext}
        />
      </Stack>
    </Box>
  );
};

// Helper components
// General
const PageDots = ({ index, total, maxReachable, onSelect }) => {
  return (
    <Stack direction="row" spacing={1.5} justifyContent="center">
      {Array.from({ length: total }, (_, arrayIndex) => {
        const reachable = arrayIndex <= maxReachable;
        return (
          <ButtonBase
            key={arrayIndex}
            disabled={!reachable}
            aria-label={`Go to page ${arrayIndex + 1}`}
            aria-current={index === arrayIndex ? "step" : undefined}
            onClick={() => onSelect(arrayIndex)}
            sx={{
              // 32px tap target around the 16px visible dot
              width: 32,
              height: 32,
              borderRadius: "50%",
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor:
                  index === arrayIndex
                    ? "text.primary"
                    : reachable
                      ? "action.disabled"
                      : "action.disabledBackground",
              }}
            />
          </ButtonBase>
        );
      })}
    </Stack>
  );
};
const NextButton = ({ label, disabled, onTap }) => {
  return (
    <Button
      variant="contained"
      fullWidth
      size="large"
      disabled={disabled}
      onClick={onTap}
      sx={{
        borderRadius: "50rem",
        fontWeight: "bold",
        textTransform: "none",
        py: 1.5,
      }}
    >
      {label}
    </Button>
  );
};

// The view prop types
PrescanSlideView.propTypes = {
  bodyPart: PropTypes.string.isRequired,
  onBack: PropTypes.func,
  onContinue: PropTypes.func.isRequired,
  onPermissionGranted: PropTypes.func.isRequired,
  motionPermission: PropTypes.string.isRequired,
  onRequestMotion: PropTypes.func,
  initialScanPov: PropTypes.oneOf([
    SCAN_FOR_SELF,
    SCAN_FOR_OTHER,
    SCAN_USER_SET,
  ]),
  mayUsePaper: PropTypes.bool.isRequired,
  onScanPovSet: PropTypes.func.isRequired,
  onPaperTypeSet: PropTypes.func.isRequired,
};
PageDots.propTypes = {
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  maxReachable: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
};
NextButton.propTypes = {
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  onTap: PropTypes.func.isRequired,
};
